import type { Api } from '../api';

import { parseISO } from 'date-fns';

import * as TicketLabel from './TicketLabel';

/** A half-open window, e.g. one calendar month or week. */
export type ReportPeriod = {
  start: Date;
  end: Date;
};

/** One ticket row in a report. */
export type ReportTicket = Api.TicketReport.V['tickets'][number];

export function isInPeriod(date: string | null, period: ReportPeriod): boolean {
  if (!date) return false;
  const parsed = parseISO(date);
  return parsed >= period.start && parsed < period.end;
}

/**
 * The ticket was raised inside the period.  Opened and Updated are exclusive,
 * so a ticket opened in the period never also counts as updated in it.
 */
export function isOpenedInPeriod(
  report: ReportTicket,
  period: ReportPeriod
): boolean {
  return isInPeriod(report.ticket.createdAt, period);
}

/**
 * A ticket counts as updated when work was logged in the period.  The backend
 * only returns tickets with time inside the reported period, so everything
 * that was not opened in the period counts as updated.
 */
export function isUpdatedInPeriod(
  report: ReportTicket,
  period: ReportPeriod
): boolean {
  return !isOpenedInPeriod(report, period);
}

/** The resolution label currently on the ticket, if any. */
export function getResolutionLabel(
  report: ReportTicket
): Api.TicketLabel.BaseView | null {
  return report.ticket.labels.find(TicketLabel.isResolution) ?? null;
}

/** Minutes logged against the ticket inside the reported period. */
export function getMinutesInPeriod(report: ReportTicket): number {
  return report.minutes;
}

export type ReportStatusVariant = 'resolved' | 'blueGrey' | 'open';

export type ReportStatus = {
  label: string;
  variant: ReportStatusVariant;
};

/**
 * The chip shown for a ticket in the report.  A ticket with no resolution
 * gets a quiet grey Open chip; of the resolutions, only "resolved" earns the
 * blue one, and every other resolution keeps its own words on the shared
 * blue-grey chip.
 */
export function getReportStatus(report: ReportTicket): ReportStatus {
  const resolution = getResolutionLabel(report);
  if (!resolution) return { label: 'Open', variant: 'open' };
  return {
    label: resolution.name,
    variant:
      resolution.name.toLowerCase() === 'resolved' ? 'resolved' : 'blueGrey',
  };
}
